import React, { useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Controller, UseFormReturn, useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { Modal } from '../../../../components/Modal/Modal'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { OAuth2App, ShortOAuth2App } from '../../../../store/auth/auth'

import { useOAuth2AppRolePermissions } from '../hooks'
import { COMBINED_ROLE_GROUPS } from '../utils'
import { useGetRolesQuery } from '../../../../store/role/role'
import { addToast } from '../../../../store/toast/toastSlice'

interface EnableOAuth2AppModalProps {
  oAuth2App: ShortOAuth2App | OAuth2App
  show: boolean
  onHide: () => void
}

interface FormValues {
  modalRoleName: string
}

type Step = 'form' | 'success'

interface EnableOAuth2AppFormStepProps {
  appName: string
  form: UseFormReturn<FormValues>
}

interface EnableOAuth2AppSuccessStepProps {
  appName: string
  enabledRoleName: string
}

const EnableOAuth2AppFormStep: React.FC<EnableOAuth2AppFormStepProps> = ({ appName, form }) => {
  const { control } = form

  return (
    <Container>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Select which roles can access {appName}.</div>
        </Col>
      </Row>

      <Row className='pb-4'>
        <Col className='p-0'>
          <Controller
            name='modalRoleName'
            control={control}
            render={({ field }) => (
              <>
                {COMBINED_ROLE_GROUPS.map((group) => (
                  <Form.Check
                    key={group.name}
                    inline
                    type='radio'
                    className='pb-2'
                    label={group.name}
                    id={`modal_${group.name}`}
                    {...field}
                    value={group.name}
                    checked={field.value === group.name}
                  />
                ))}
              </>
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}

const EnableOAuth2AppSuccessStep: React.FC<EnableOAuth2AppSuccessStepProps> = ({ appName, enabledRoleName }) => {
  return (
    <Container>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>{appName} is now available with this role:</div>
        </Col>
      </Row>

      <Row className='pb-4'>
        <Col className='d-flex flex-column p-0 gap-1'>
          <div className='d-flex gap-2'>
            <i className='bi bi-check2 zocks-text-success' />
            {enabledRoleName}
          </div>
        </Col>
      </Row>

      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>Default permissions are applied. We recommend reviewing them before users start connecting.</div>
        </Col>
      </Row>
    </Container>
  )
}

export const EnableOAuth2AppModal: React.FC<EnableOAuth2AppModalProps> = ({ oAuth2App, show, onHide }) => {
  const { name: appName, clientId, id: appId } = oAuth2App

  const initialSelectedRadioButton = COMBINED_ROLE_GROUPS[0].name

  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('form')
  const [enabledRoleNames, setEnabledRoleNames] = useState<string>(initialSelectedRadioButton)

  const form = useForm<FormValues>({
    defaultValues: { modalRoleName: initialSelectedRadioButton },
  })

  const { control, handleSubmit, reset } = form

  const {
    data: rolesResponse,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
  } = useGetRolesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { updatePermissions, isUpdating } = useOAuth2AppRolePermissions({
    accountId,
    appId,
    accountEnabled: true,
    errorToast: {
      title: 'Error',
      messages: ['Failed to enable app. Please try again.'],
    },
  })

  const resetModalState = () => {
    setStep('form')
    setEnabledRoleNames(initialSelectedRadioButton)
    reset({ modalRoleName: initialSelectedRadioButton })
  }

  const handleClose = () => {
    resetModalState()
    onHide()
  }

  const selectedRoleName = useWatch({ control, name: 'modalRoleName' })
  const disablePrimaryButton = !selectedRoleName || isLoadingRoles || isFetchingRoles || !rolesResponse

  const handleEnable = async (values: FormValues) => {
    if (!rolesResponse) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Error',
          messages: ['Failed to enable app. Please try again.'],
        }),
      )
      return
    }
    const roleIds = COMBINED_ROLE_GROUPS.find((roleGroup) => roleGroup.name === values.modalRoleName)?.roleIds

    const result = await updatePermissions({
      selectedRoleIds: roleIds ?? [],
      allSystemRoles: rolesResponse.data.map((role) => role.id),
    })
    if (!result.ok) {
      return
    }
    setEnabledRoleNames(values.modalRoleName)
    setStep('success')
  }

  const handleReviewPermissions = () => {
    handleClose()
    navigate(`/account/integration-settings/oauth2apps/${clientId}/permissions`)
  }

  const isFormStep = step === 'form'

  return (
    <Modal
      show={show}
      onHide={handleClose}
      header={<h4 className='p-2'>{isFormStep ? `Enable ${appName} for all users?` : `${appName} enabled`}</h4>}
      body={
        isFormStep ? (
          <EnableOAuth2AppFormStep appName={appName} form={form} />
        ) : (
          <EnableOAuth2AppSuccessStep appName={appName} enabledRoleName={enabledRoleNames} />
        )
      }
      showFooterActions={true}
      onFooterPrimaryAction={isFormStep ? handleSubmit(handleEnable) : handleReviewPermissions}
      footerPrimaryActionLoading={isFormStep ? isUpdating : false}
      footerPrimaryActionTitle={isFormStep ? 'Enable' : 'Review permissions'}
      footerPrimaryActionButtonVariant='primary'
      footerSecondaryActionTitle={isFormStep ? 'Cancel' : 'Done'}
      footerPrimaryActionDisabled={disablePrimaryButton}
    />
  )
}
